<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { logout } from '@accor/customer-api/cloud'
import type { AccorCurrencies } from '@accor/core-services/src/pos'
import { storeToRefs } from 'pinia'
import { i18nService } from '@ecom/customer-common-front'
import { getHeaderItems, type HeaderItemsResponse } from '@/services/aem'
import { useWebAppStore } from '@/stores/webApp'
import { useUserStore } from '@/stores/user'
import { useRoute, useRouter } from 'vue-router'
import {
  getSelectedGeolocation,
  getAccorGeolocations,
  updateGeolocation
} from '@/services/geolocationService'
import { getSelectedCurrency, getAccorCurrencies, updateCurrency } from '@/services/currencyService'
import type { SelectedActionData, SelectedCurrency } from '@/services/currencyService'
import type { SelectedGeolocation, AccorGeoLocations } from '@/services/geolocationService'
import AhHeader from '@accor/ah-navigation/src/components/AhHeader/AhHeader.vue'
import LoginNav from '@accor/login-nav/src/components/LoginNav.vue'
import RelatedDropdown from '@accor/related-dropdown/src/components/RelatedDropdown.vue'

const router = useRouter()
const route = useRoute()

const webAppStore = useWebAppStore()
const userStore = useUserStore()

const {
  username,
  rewardsPoints,
  last12MonthRewardsPoints,
  productTier,
  balances,
  loyaltyCard,
  hasWallet
} = storeToRefs(userStore)

const listItems = ref<HeaderItemsResponse>([])
const locale = computed(() => i18nService.getCurrentLocale())
const selectedGeolocation = ref<SelectedGeolocation>({})
const accorGeolocations = ref<AccorGeoLocations>({})
const selectedCurrency = ref<SelectedCurrency>({})
const accorCurrencies = ref<AccorCurrencies>({})

onMounted(async () => {
  selectedGeolocation.value = await getSelectedGeolocation()
  accorGeolocations.value = await getAccorGeolocations()

  selectedCurrency.value = await getSelectedCurrency()
  accorCurrencies.value = await getAccorCurrencies()

  listItems.value = await getHeaderItems(locale.value)
})

const actionCallbacks = {
  disconnectAction: async () => {
    await logout()
    window.location.href = '/a/select-country.html'
  }
}

const updateGeolocationAndRedirect = (selectedData: SelectedActionData) =>
  updateGeolocation(selectedData, route, router)

const userInfo = computed(() => {
  if (!webAppStore.isUserInfoReady) {
    return null
  }

  return {
    username: username.value,
    points: rewardsPoints.value,
    statusPoints: last12MonthRewardsPoints.value,
    nights: balances.value.currentNightsBalance || 0,
    statut: productTier.value,
    currentNightsBalance: balances.value.currentNightsBalance || 0,
    endDate: loyaltyCard.value?.endDate || null,
    hasWallet: hasWallet.value
  }
})
</script>

<template>
  <AhHeader
    v-if="listItems.length"
    :list-items="listItems"
    :lang="locale"
    :geoloc="selectedGeolocation?.geolocationItem"
    :currency="selectedCurrency.currencyItem"
    is-sticky
  >
    <template #loginnav>
      <LoginNav
        :lang="locale"
        :is-loading="!webAppStore.isUserInfoReady"
        :is-connected="webAppStore.isUserInfoReady"
        :is-member="!!loyaltyCard"
        :status-info="userInfo"
        :action-callbacks="actionCallbacks"
      />
    </template>
    <template v-if="accorGeolocations.geolocOptionsData" #geoloc>
      <RelatedDropdown
        :sdd-options="accorGeolocations.geolocOptionsData"
        :first-sdd-selected="selectedGeolocation.geolocationDropdown.continent"
        :second-sdd-selected="selectedGeolocation.geolocationDropdown.selectedOption"
        :lang="locale"
        @get-relateddropdown-data="updateGeolocationAndRedirect"
      />
    </template>
    <template v-if="accorCurrencies.currencies" #currency>
      <RelatedDropdown
        trad-context-pre="currencySelector"
        :sdd-options="accorCurrencies.currencies"
        :first-sdd-selected="selectedCurrency.currencyDropdown.continent"
        :second-sdd-selected="selectedCurrency.currencyDropdown.currency"
        :lang="locale"
        @get-relateddropdown-data="updateCurrency"
      />
    </template>
  </AhHeader>
</template>
